import { Dispatch } from 'redux';
import { updateAppVersion } from '../stores/actions/session';
import type { ShotData } from '../stores/types/shots';
import type { MediaItem } from '../stores/types/media';
import { DRAG_TYPE } from '../stores/reducers/user-interface';
import { appLogger } from '../utils/datadog';
import { HttpRequest } from '../stores/types/types';
import { roleAndMeetingSlug } from '../stores/session-status';
import { URL_STRINGS } from '../stores/types/session';
import blpAuth from './blp-auth';

interface SyncErrorResponse {
	response?: Response;
	errorMessage?: string;
}

export const getHeaders = (token: string = ''): HeadersInit => {
	const { role } = roleAndMeetingSlug();

	let usedToken = token;
	if (!usedToken || usedToken === '') {
		try {
			usedToken = JSON.parse(localStorage.getItem('token') ?? '{}')?.item;
		} catch (e: any) {
			appLogger.error('error parsing token', {}, e);
		}
	}

	const additionalHeaders: Record<string, string> = {};
	if (role === URL_STRINGS.EMBED) {
		additionalHeaders['X-IS-EMBED'] = 'true';
	}

	if (role === URL_STRINGS.PRODUCER) {
		additionalHeaders['X-IS-PRODUCER'] = 'true';
	}

	return {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'BL-TOKEN': token,
		...additionalHeaders,
	};
};

/**
 * Creates a RequestInit object with authentication headers for API requests.
 * Includes both the BL token and unified auth access token if available.
 *
 * @param token - The BL token for authentication
 * @returns RequestInit object configured with authentication headers
 */
export const getAuthenticatedRequestInit = (token: string): RequestInit => {
	const accessToken = blpAuth.accessToken || sessionStorage.getItem('bl#accessToken') || '';

	return {
		headers: {
			...getHeaders(token),
			...(accessToken && { Authorization: `Bearer ${accessToken}` }),
		},
		credentials: accessToken ? undefined : 'include',
	};
};

export const getSyncHeaders = (token: string): HeadersInit => ({
	Accept: 'application/json',
	'Content-Type': 'application/json',
	'bl-token': token,
});

export const FetchFeatureFlags = async () => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flags`, {
	method: 'POST',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
}).then(async (result) => {
	if (result.ok) {
		const featureFlags = await result.json();
		return featureFlags;
	}
	appLogger.error('Unable to get feature flags.');
	return {};
}).catch((error) => {
	appLogger.error('error fetching feature flags', {}, error);
});

export const FetchFeatureFlagOverrides = async (meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flag-overrides/meeting/${meeting}`, {
	method: 'GET',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
}).then(async (result) => {
	if (result.ok) {
		const overrides = await result.json();
		return overrides;
	}
	appLogger.error('Unable to get feature flag overrides.');
	return {};
}).catch((error) => {
	appLogger.error('error fetching feature flag overrides', {}, error);
});

export const buildSyncUrl = (route: string) => {
	const { REACT_APP_SYNC_HOST = '' } = process.env;

	if (!REACT_APP_SYNC_HOST) {
		appLogger.error('Missing `REACT_APP_SYNC_HOST`.');
	}

	const syncHost = REACT_APP_SYNC_HOST.replace(/\/$/, '');
	const buildRoute = route.replace(/^\//, '');

	return `${syncHost}/${buildRoute}`;
};

export const handleResponseError = async ({
	response,
	errorMessage = 'unknown error',
}: SyncErrorResponse): Promise<null> => {
	let errorDetails: string = errorMessage;

	if (response) {
		errorDetails = await response.text();
	}

	appLogger.error(errorDetails);

	return null;
};

export const getCurrentVersion = async (token: string, dispatch: Dispatch) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/current-version`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		if (data.version) {
			return dispatch(updateAppVersion(data?.version));
		}
	}
	appLogger.error('Unable to request current version.');
	return null;
}).catch((error) => {
	appLogger.error('getCurrentVersion error', {}, error);
});

export const getValidShotData = async (shotData: ShotData, shotMedia: MediaItem[]) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/validate-shot`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ shotData, shotMedia }),
		});
		if (response.ok) {
			return response.json();
		}
		appLogger.error('could not validate shot');
		return null;
	} catch (error: any) {
		appLogger.error('error validating shot', {}, error);
		return null;
	}
};

export const validateShotData = async (shotData: ShotData, media: MediaItem[]) => {
	const shotMedia: MediaItem[] = [];
	shotData.shotFeeds.forEach((feed) => {
		if (feed.feedType === DRAG_TYPE.FEED_MEDIA) {
			const item = media.find((m: MediaItem) => m.id === feed.media);
			if (item) {
				shotMedia.push(item);
			}
		}
	});
	const validatedShotData = await getValidShotData(shotData, shotMedia);
	if (validatedShotData) {
		return validatedShotData;
	}
	return shotData;
};

export const AddFeatureFlagOverride = async (
	token: string,
	featureFlag: string,
	meetingId: number,
	flagState: boolean,
): Promise<boolean> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flag-override/meeting/${meetingId}`,
			{
				method: HttpRequest.Post,
				body: JSON.stringify({
					featureFlag,
					flagState,
				}),
				...getAuthenticatedRequestInit(token),
			},
		);

		if (response.ok) {
			return true;
		}

		appLogger.error(`AddFeatureFlagOverride error: ${response.status}`);
	} catch (error) {
		appLogger.error(`AddFeatureFlagOverride error: ${error}`);
	}

	return false;
};

export type reasonForNoAccess = String & 'roomWasDeleted' | 'roomDoesNotExist' | 'notInvited'
export const sendUserToNoAccessView = (reason: reasonForNoAccess) => {
	console.warn(`User tried to access Greenroom but was denied access, redirecting to home view with access denied for reason: ${reason}`)
	window.history.pushState(null, 'Greenroom', `/?accessDenied=true&reason=${reason}`);
}